import { Alert, Checkbox, Divider, Form, Input, InputNumber, Modal, Radio, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  defaultHelpAnnounceOptions,
  emptyHelpAnnounceTarget,
  isHelpAnnouncementLive,
  helpAnnounceBlockers,
  HELP_ANNOUNCE_DEFAULT_CTA,
  type HelpAnnounceOptions,
  type HelpAnnounceTargetOptions,
  type HelpArticleEntity,
} from "@fitness-app/data-models/entities/HelpArticle";
import { ProductType } from "@fitness-app/data-models/entities/Product";
import { isAnnouncementPushable } from "@fitness-app/utils/src/help/helpAnnouncement";

import { useTagOptions } from "~/hooks/useTagOptions";
import { useProductTaskAutomation } from "~/modules/Products/hooks/useProductTaskAutomation";

import { useHelpAnnouncementTarget } from "./useHelpCenter";

interface PublishHelpArticleModalProps {
  article: HelpArticleEntity | null;
  open: boolean;
  loading: boolean;
  /**
   * `publish` — publikacja materiału, przy okazji której zapada decyzja o
   * ogłoszeniu. `announce` — materiał jest już opublikowany i chodzi wyłącznie
   * o (ponowne) ogłoszenie, bez cofania publikacji.
   */
  mode?: "publish" | "announce";
  onCancel: () => void;
  onConfirm: (announce: HelpAnnounceOptions | null) => void;
}

/**
 * Gdzie klientka zobaczy popup — w słowach redakcji, nie w kodach platform.
 * Lustro reguły z `isHelpAnnouncementVisibleTo`: przeglądarka pokazuje tylko
 * wpisy z `web`, aplikacja — z `mobile` albo konkretnym systemem.
 */
const announcementSurfaces = (platforms: string[]): string => {
  const surfaces: string[] = [];
  const ios = platforms.includes("ios");
  const android = platforms.includes("android");

  if (platforms.includes("mobile") || (ios && android)) {
    surfaces.push("aplikacji");
  } else if (ios) {
    surfaces.push("aplikacji na iOS");
  } else if (android) {
    surfaces.push("aplikacji na Androidzie");
  }

  if (platforms.includes("web")) {
    surfaces.push("przeglądarce");
  }

  return surfaces.join(" i ");
};

/**
 * Publikacja wpisu „Co nowego” z decyzją o ogłoszeniu. Dwie osobne rzeczy w
 * jednym oknie, bo w praktyce zapada tu jedna decyzja redakcyjna: „wypuszczamy
 * i mówimy o tym klientkom”. Instrukcje publikują się bez tego okna — ogłaszać
 * można wyłącznie wpisy „Co nowego”.
 *
 * Materiał ogłoszony wcześniej nie dostaje checkboxów, tylko datę i przycisk
 * „Ogłoś ponownie”: poprawienie literówki w opublikowanym wpisie nie ma prawa
 * wysłać popupu drugi raz.
 */
export const PublishHelpArticleModal = ({
  article,
  open,
  loading,
  mode = "publish",
  onCancel,
  onConfirm,
}: PublishHelpArticleModalProps) => {
  const [form] = Form.useForm<HelpAnnounceOptions>();
  const announce = Form.useWatch("announce", form);
  const push = Form.useWatch("push", form);
  const selectedTags = Form.useWatch(["target", "tags"], form);
  const alreadyAnnounced = !!article?.announcedAt;
  // Wpis wyłącznie o przeglądarce nie ma pusha: push jest mobilny, a aplikacja
  // odrzuciłaby taki wpis po platformie — serwis i tak by go pominął.
  const pushable = article ? isAnnouncementPushable(article.platforms) : true;
  const { t } = useTranslation("common");
  const { options: tagOptions } = useTagOptions("traineeTags");
  const { productWithPrices } = useProductTaskAutomation();

  // Kryteria poprzedniego ogłoszenia. Bez nich okno startowało od pustych list,
  // więc „Ogłoś ponownie” kliknięte bez zmian zamieniało pilotaż dla jednego
  // tagu w popup i push do całej bazy.
  const target = useHelpAnnouncementTarget(open && article && alreadyAnnounced ? article.id : null);
  const targetPending = alreadyAnnounced && target.isLoading;
  // Formularz wypełniamy RAZ na otwarcie — późniejszy powrót zapytania nie może
  // nadpisać tego, co redakcja zdążyła już zmienić.
  const filledFor = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !article) {
      filledFor.current = null;
      return;
    }

    if (targetPending || filledFor.current === article.id) {
      return;
    }

    const previousTarget: HelpAnnounceTargetOptions = target.data
      ? {
          productIds: target.data.productIds ?? [],
          productTypes: target.data.productTypes ?? [],
          tags: target.data.tags ?? [],
          tagsCondition: (target.data.tagsCondition as HelpAnnounceTargetOptions["tagsCondition"]) ?? null,
        }
      : emptyHelpAnnounceTarget();

    form.setFieldsValue({
      ...defaultHelpAnnounceOptions(article.kind as "guide" | "update"),
      // Przy publikacji powtórne ogłoszenie zaczyna od „nie”: domyślnie
      // wypuszczamy poprawkę, a nie zawiadamiamy wszystkich po raz drugi.
      // W trybie „Ogłoś” pytanie jest odwrotne — po to otwarto okno.
      announce: mode === "announce" || !alreadyAnnounced,
      push: pushable && (mode === "announce" || !alreadyAnnounced),
      ctaText: article.announceCtaText || HELP_ANNOUNCE_DEFAULT_CTA,
      target: previousTarget,
    });
    filledFor.current = article.id;
  }, [open, article?.id, alreadyAnnounced, mode, targetPending, target.data, pushable]);

  // Przy publikacji bramkę „najpierw opublikuj” spełnia ta sama akcja, która
  // otwiera to okno — dlatego status liczy się jako `published`. Przy samym
  // ogłoszeniu sprawdzamy stan faktyczny.
  const blockers = article
    ? helpAnnounceBlockers({
        kind: article.kind,
        status: mode === "publish" ? "published" : article.status,
        announcedAt: article.announcedAt,
      })
    : [];

  return (
    <Modal
      open={open}
      title={mode === "publish" ? "Opublikować materiał?" : "Ogłosić materiał klientkom?"}
      okText={mode === "publish" ? "Publikuj" : "Ogłoś"}
      cancelText="Anuluj"
      // Dopóki nie wiemy, do kogo szło poprzednie ogłoszenie, nie da się go
      // świadomie powtórzyć — przycisk czeka na kryteria.
      okButtonProps={{ disabled: (mode === "announce" && blockers.length > 0) || targetPending }}
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() => {
        if (blockers.length) {
          onConfirm(null);
          return;
        }

        // Bez walidacji wyczyszczone „dni widoczności” szły jako `null`, a baza
        // odbijała publikację checkiem `help_article_announce_window` — czyli
        // poprawny materiał nie dawał się opublikować przez puste pole.
        void form.validateFields().then(
          () => onConfirm(form.getFieldsValue(true)),
          () => undefined,
        );
      }}
    >
      {alreadyAnnounced && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Ogłoszono ${dayjs(article?.announcedAt).format("DD.MM.YYYY HH:mm")}`}
          description={
            article?.announcePushSentAt
              ? // Dosłanie jest w menu tylko przy trwającym ogłoszeniu - po
                // wygaśnięciu jedyną drogą jest nowe ogłoszenie.
                article && isHelpAnnouncementLive(article)
                ? "Push już poszedł. Kolejne ogłoszenie pokaże popup ponownie, także tym, które już go widziały. Żeby wysłać samo powiadomienie tym, które go nie dostały, zamknij to okno i użyj „Więcej → Doślij powiadomienie push” przy wpisie."
                : "Push już poszedł, a ogłoszenie wygasło. Kolejne ogłoszenie pokaże popup ponownie, także tym, które już go widziały, i może wysłać nowy push."
              : "Kolejne ogłoszenie pokaże popup ponownie — także tym, które już go widziały."
          }
        />
      )}

      {blockers.length > 0 ? (
        <Typography.Paragraph type="secondary">
          {mode === "publish"
            ? `Materiał zostanie opublikowany bez ogłoszenia klientkom. ${blockers.join(" ")}`
            : blockers.join(" ")}
        </Typography.Paragraph>
      ) : (
        <Form form={form} layout="vertical" initialValues={defaultHelpAnnounceOptions("update")}>
          {/* Platformy wpisu decydują, gdzie popup w ogóle wyskoczy — bez tej
              linijki łatwo ogłosić nowość tylko o aplikacji i szukać jej
              w przeglądarce. */}
          {article && (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Popup pokaże się w {announcementSurfaces(article.platforms)}. Platformy zmienisz w ustawieniach materiału.
            </Typography.Paragraph>
          )}

          <Form.Item name="announce" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>Pokaż popup klientkom</Checkbox>
          </Form.Item>

          {/* Sam push prowadziłby na ekran główny, gdzie nic nie czeka — popup
              liczy się u klientki z ogłoszonego wpisu. */}
          <Form.Item
            name="push"
            valuePropName="checked"
            style={{ marginBottom: 16 }}
            extra={
              pushable
                ? undefined
                : "Push nie pójdzie — wpis dotyczy tylko przeglądarki, a powiadomienia są w aplikacji."
            }
          >
            <Checkbox disabled={!announce || !pushable}>Wyślij też powiadomienie push</Checkbox>
          </Form.Item>

          {/* Popup i zwykły push widzi tylko klientka z wydaniem, w którym jest
              ta zmiana. Przy wydaniu ze sklepu, ogłoszonym od razu, to zwykle
              garstka - reszta nie dostałaby nic (TRE-2259). */}
          <Form.Item
            name="pushAudience"
            label="Do kogo push"
            style={{ marginBottom: 16 }}
            extra="Klientki ze starszą wersją nie zobaczą popupu, dopóki nie zaktualizują aplikacji."
          >
            <Radio.Group disabled={!announce || !push || !pushable}>
              <Space direction="vertical">
                <Radio value="all">Wszystkie: starsze wersje dostaną prośbę o aktualizację aplikacji</Radio>
                <Radio value="release">Tylko klientki, które mają już tę wersję aplikacji</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="ctaText" label="Tekst przycisku">
            <Input disabled={!announce} placeholder={HELP_ANNOUNCE_DEFAULT_CTA} maxLength={40} />
          </Form.Item>

          <Form.Item
            name="visibleForDays"
            label="Popup widoczny przez (dni)"
            extra="Po tym czasie popup gaśnie sam — nowość sprzed dwóch miesięcy nie ma witać nowej klientki."
            rules={[
              {
                required: announce,
                type: "number",
                min: 1,
                max: 60,
                message: "Podaj od 1 do 60 dni.",
              },
            ]}
          >
            <InputNumber disabled={!announce} min={1} max={60} />
          </Form.Item>

          {/* Nie każda nowość jest dla wszystkich: funkcja bywa wpięta w jeden
              produkt albo w pilotaż oznaczony tagiem. Puste pola = wszystkie
              klientki; wypełnione zawężają iloczynem. */}
          <Divider orientation="left" plain style={{ marginTop: 8 }}>
            Do kogo (opcjonalnie)
          </Divider>

          <Form.Item name={["target", "productIds"]} label="Tylko produkty">
            <Select
              disabled={!announce}
              mode="multiple"
              allowClear
              maxTagCount={4}
              placeholder="Wszystkie produkty"
              optionFilterProp="label"
              options={productWithPrices.map((product) => ({ value: product.id, label: product.name }))}
            />
          </Form.Item>

          <Form.Item name={["target", "productTypes"]} label="Tylko typy produktów">
            <Select
              disabled={!announce}
              mode="multiple"
              allowClear
              placeholder="Wszystkie typy"
              options={Object.values(ProductType).map((value) => ({
                value,
                label: t(`common:productType.${value}`),
              }))}
            />
          </Form.Item>

          <Form.Item name={["target", "tags"]} label="Tagi klientek">
            <Select
              disabled={!announce}
              mode="multiple"
              allowClear
              placeholder="Bez filtra tagów"
              options={tagOptions}
              // Warunek bez wartości znaczy „bez zawężenia tagami”, więc wybór
              // tagów bez niego rozesłałby ogłoszenie do WSZYSTKICH — odwrotnie
              // niż chce osoba wybierająca tag pilotażu. Stąd domyślka od razu
              // przy pierwszym tagu (i reguła niżej, i check w bazie).
              onChange={(tags: string[]) => {
                if (tags.length && !form.getFieldValue(["target", "tagsCondition"])) {
                  form.setFieldValue(["target", "tagsCondition"], "hasTag");
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name={["target", "tagsCondition"]}
            label="Warunek tagów"
            rules={[{ required: announce && !!selectedTags?.length, message: "Wybierz warunek dla tagów." }]}
          >
            <Select
              disabled={!announce || !selectedTags?.length}
              placeholder="Wybierz warunek"
              options={[
                { value: "hasTag", label: "Ma któryś z tagów" },
                { value: "notHasTag", label: "Nie ma żadnego z tagów" },
              ]}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
